import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import { Alert, Box, Stack } from '@mui/material';
import { withScope, captureException } from '@sentry/react';
import Logo from '@/common/presentation/components/layout/Logo';
import styles from './RouteErrorElement.module.scss';

/**
 * Router `errorElement`: catches a render-time or loader throw anywhere under the route it is
 * attached to, so the recipient sees a branded message instead of a blank page. Report the raw
 * error to Sentry for diagnostics only — never render it to the user (see LLAMS-562 L4).
 *
 * Limits: this only catches render and loader throws, not errors thrown inside async event
 * handlers (those remain `useSnackbar`'s job).
 */
const RouteErrorElement: React.FC = () => {
  const error = useRouteError();

  useEffect(() => {
    withScope((scope) => {
      scope.setExtra('error_type', 'route-error-element');
      captureException(error);
    });
  }, [error]);

  return (
    <Box className={styles.wrapper}>
      <Stack className={styles.header}>
        <Logo expanded={true} clientLogo={null} loading={false} />
      </Stack>
      <Stack className={styles.content}>
        <Alert variant="outlined" severity="error" sx={{ maxWidth: 600 }}>
          Something went wrong loading this page. Please contact the sender for a new link.
        </Alert>
      </Stack>
    </Box>
  );
};

export default RouteErrorElement;
